<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import { BaseButton, FieldSelect } from '@magma-app/magma-lapilli';

import type {
  SlideOverDeactivationForm,
  SlideOverOptOutReason,
  SlideOverReactivationIn,
  SlideOverSelectOption,
} from './types';

const props = defineProps<{
  deactivateCampaignUnstartedRelations: number;
  form: SlideOverDeactivationForm;
  options: {
    keepUnstartedRelations: SlideOverSelectOption<boolean>[];
    optOutReason: SlideOverSelectOption<SlideOverOptOutReason>[];
    reactivationIn: SlideOverSelectOption<SlideOverReactivationIn>[];
  };
  validation: {
    deactivation: {
      $invalid: boolean;
      $error: boolean;
    };
  };
  isLoading: boolean;
}>();

const emit = defineEmits<{
  (event: 'confirm'): void;
  (event: 'cancel'): void;
}>();

const { t } = useI18n({ useScope: 'global' });

const keepUnstartedRelationsDescription = computed(() => {
  const count = props.deactivateCampaignUnstartedRelations;
  const suffixKey =
    count > 1
      ? 'slideOverModal.deactivate.keepUnstartedRelations.description.partTwo.plural'
      : 'slideOverModal.deactivate.keepUnstartedRelations.description.partTwo.singular';

  return `${t('slideOverModal.deactivate.keepUnstartedRelations.description.partOne')}${count}${t(suffixKey)}`;
});
</script>

<template>
  <div>
    <FieldSelect
      v-if="deactivateCampaignUnstartedRelations > 0"
      id="keepUnstartedRelations"
      v-model="form.keepUnstartedRelations"
      :description="keepUnstartedRelationsDescription"
      :label="$t('slideOverModal.deactivate.keepUnstartedRelations.label')"
      :options="options.keepUnstartedRelations"
      :placeholder="$t('generic.placeholder.singleSelect')"
      required
    />

    <FieldSelect
      id="optOutReason"
      v-model="form.optOutReason"
      :class="{
        'mt-4': deactivateCampaignUnstartedRelations > 0
      }"
      :label="$t('slideOverModal.deactivate.optOutReason.label')"
      :options="options.optOutReason"
      :placeholder="$t('generic.placeholder.singleSelect')"
      required
    />

    <FieldSelect
      id="reactivationIn"
      v-model="form.reactivationIn"
      class="mt-4"
      :label="$t('slideOverModal.deactivate.reactivationIn.label')"
      :options="options.reactivationIn"
      :placeholder="$t('generic.placeholder.singleSelect')"
      required
    />

    <BaseButton
      class="mt-6 w-full"
      :text="$t('generic.confirm')"
      size="md"
      :disabled="
        validation.deactivation.$invalid ||
        validation.deactivation.$error ||
        isLoading
      "
      @click="emit('confirm')"
    />

    <BaseButton
      class="mt-2 w-full"
      :text="$t('generic.cancel')"
      size="md"
      state="scdr"
      @click="emit('cancel')"
    />
  </div>
</template>
